import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Shield, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function SuperAdminAdmins() {
  const { user } = useAuth();
  const [data, setData] = useState({ limit: 2, count: 0, admins: [] });
  const load = () => api.get("/superadmin/super-admins").then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const remaining = data.limit - data.count;

  const del = async (id) => {
    if (!confirm("Supprimer ce Super Admin ?")) return;
    try { await api.delete(`/superadmin/super-admins/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Super Admins Transport.pro</h1>
          <p className="text-sm text-slate-500">Maximum <b>{data.limit}</b> comptes autorisés · <b>{data.count}</b> utilisés · <b className={remaining ? "text-emerald-600" : "text-red-600"}>{remaining}</b> restant</p>
        </div>
        {remaining > 0 && <NewAdminDialog onCreated={load} />}
      </div>

      {remaining === 0 && (
        <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <b>Limite atteinte.</b> Transport.pro autorise <b>{data.limit}</b> Super Admins au maximum. Supprimez un compte existant avant d'en ajouter un nouveau.
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {data.admins.map((a) => (
          <Card key={a.id} data-testid={`sa-admin-card-${a.email}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 grid place-items-center"><Shield className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{a.full_name}</div>
                <div className="text-xs text-slate-500 truncate">{a.email}</div>
                <div className="text-[10px] uppercase tracking-widest text-orange-500 font-semibold mt-1">Super Admin</div>
              </div>
              {a.id !== user?.id && (
                <Button variant="ghost" size="icon" onClick={() => del(a.id)} data-testid={`sa-admin-delete-${a.email}`}><Trash2 className="w-4 h-4" /></Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function NewAdminDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", password: "" });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white" data-testid="sa-admin-add-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau Super Admin</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Ajouter un Super Admin</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom complet</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="sa-admin-name" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="sa-admin-email" /></div>
          <div><Label>Mot de passe (min. 8 caractères)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sa-admin-password" /></div>
          <Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white w-full" data-testid="sa-admin-save-btn" onClick={async () => {
            try { await api.post("/superadmin/super-admins", form); toast.success("Super Admin ajouté"); setOpen(false); setForm({ email: "", full_name: "", password: "" }); onCreated(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>Créer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
